import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import SEO from '../components/SEO';
import GuestBanner from '../components/GuestBanner';
import GuestAccessibleBadge from '../components/GuestAccessibleBadge';
import PartnerLeaderboard from '../components/PartnerLeaderboard';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Handshake, Building2, Heart, Dog, Briefcase, Star,
  ExternalLink, MapPin, Globe, Mail, Phone,
  ShieldCheck, Crown, Plus, Loader2, Search, CheckCircle,
  ThumbsUp, ThumbsDown,
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_CONFIG = {
  ngo: { label: 'Rescue / NGO', icon: Heart, color: 'bg-rose-100 text-rose-800' },
  breeder: { label: 'Breeder', icon: Dog, color: 'bg-amber-100 text-amber-800' },
  business: { label: 'Business', icon: Briefcase, color: 'bg-blue-100 text-blue-800' },
  sponsor: { label: 'Sponsor', icon: Star, color: 'bg-purple-100 text-purple-800' },
};

const TIER_CONFIG = {
  basic: { label: 'Basic', icon: null, color: '' },
  verified: { label: 'Verified', icon: ShieldCheck, color: 'text-blue-600' },
  premium: { label: 'Premium', icon: Crown, color: 'text-amber-600' },
};

const PartnerCard = ({ partner, myVotes = {}, onVote, user }) => {
  const type = TYPE_CONFIG[partner.partner_type] || TYPE_CONFIG.business;
  const tier = TIER_CONFIG[partner.tier] || TIER_CONFIG.basic;
  const TypeIcon = type.icon;
  const votes = myVotes[partner.id] || [];

  return (
    <Card className="h-full hover:shadow-md transition-shadow" data-testid={`partner-${partner.id}`}>
      <CardContent className="p-5">
        <div className="flex items-start gap-3 mb-3">
          {partner.logo_url ? (
            <img src={partner.logo_url} alt={partner.organization_name} className="w-12 h-12 rounded-lg object-cover border" />
          ) : (
            <div className="w-12 h-12 rounded-lg bg-muted flex items-center justify-center">
              <TypeIcon className="w-6 h-6 text-muted-foreground" />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h3 className="font-heading font-semibold text-base truncate">{partner.organization_name}</h3>
              {partner.featured && <Star className="w-4 h-4 text-amber-500 fill-amber-500 shrink-0" />}
            </div>
            <div className="flex items-center gap-2 mt-0.5">
              <Badge className={`${type.color} text-xs`}>{type.label}</Badge>
              {tier.icon && (
                <span className={`flex items-center gap-0.5 text-xs font-medium ${tier.color}`}>
                  {React.createElement(tier.icon, { className: "w-3 h-3" })} {tier.label}
                </span>
              )}
            </div>
          </div>
        </div>
        {partner.description && (
          <p className="text-sm text-muted-foreground line-clamp-3 mb-3">{partner.description}</p>
        )}
        <div className="space-y-1 text-xs text-muted-foreground">
          {partner.city && (
            <p className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {partner.city}{partner.state ? `, ${partner.state}` : ''}</p>
          )}
          {partner.services_offered && (
            <p className="flex items-center gap-1"><Briefcase className="w-3 h-3" /> {partner.services_offered}</p>
          )}
        </div>
        <div className="flex items-center justify-between mt-3">
          <div className="flex gap-2">
            {partner.website && (
              <Button size="sm" variant="outline" asChild>
                <a href={partner.website} target="_blank" rel="noopener noreferrer">
                  <Globe className="w-3 h-3 mr-1" /> Website
                </a>
              </Button>
            )}
            {partner.contact_email && (
              <Button size="sm" variant="outline" asChild>
                <a href={`mailto:${partner.contact_email}`}>
                  <Mail className="w-3 h-3 mr-1" /> Contact
                </a>
              </Button>
            )}
          </div>
          {/* Voting */}
          <div className="flex items-center gap-1">
            <Button
              variant="ghost" size="icon" className={`h-7 w-7 ${votes.includes('like') ? 'text-green-600 bg-green-50' : ''}`}
              onClick={() => onVote(partner.id, 'like')}
              data-testid={`like-${partner.id}`}
            >
              <ThumbsUp className={`w-3.5 h-3.5 ${votes.includes('like') ? 'fill-current' : ''}`} />
            </Button>
            {(partner.likes || 0) > 0 && <span className="text-xs text-green-600 font-medium">{partner.likes}</span>}
            <Button
              variant="ghost" size="icon" className={`h-7 w-7 ${votes.includes('dislike') ? 'text-red-500 bg-red-50' : ''}`}
              onClick={() => onVote(partner.id, 'dislike')}
              data-testid={`dislike-${partner.id}`}
            >
              <ThumbsDown className={`w-3.5 h-3.5 ${votes.includes('dislike') ? 'fill-current' : ''}`} />
            </Button>
            <Button
              variant="ghost" size="icon" className={`h-7 w-7 ${votes.includes('heart') ? 'text-rose-500 bg-rose-50' : ''}`}
              onClick={() => onVote(partner.id, 'heart')}
              data-testid={`heart-${partner.id}`}
            >
              <Heart className={`w-3.5 h-3.5 ${votes.includes('heart') ? 'fill-current' : ''}`} />
            </Button>
            {(partner.hearts || 0) > 0 && <span className="text-xs text-rose-500 font-medium">{partner.hearts}</span>}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const PartnerDirectory = () => {
  const { user } = useAuth();
  const [partners, setPartners] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [applyOpen, setApplyOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    organization_name: '', partner_type: 'business', website: '',
    description: '', contact_name: '', contact_email: '',
    contact_phone: '', city: '', state: '', country: 'US',
    services_offered: '', years_in_operation: null, social_media: '',
    tax_exempt_id: '',
  });
  const [myVotes, setMyVotes] = useState({});

  const fetchPartners = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (typeFilter !== 'all') params.partner_type = typeFilter;
      const res = await axios.get(`${API}/partner-program`, { params });
      setPartners(res.data.partners || []);
    } catch (err) {
      console.error('Failed to fetch partners:', err);
    } finally {
      setLoading(false);
    }
  }, [typeFilter]);

  const fetchStats = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/partner-program/stats`);
      setStats(res.data);
    } catch {}
  }, []);

  useEffect(() => { fetchPartners(); fetchStats(); }, [fetchPartners, fetchStats]);

  // Fetch user votes
  useEffect(() => {
    if (user) {
      axios.get(`${API}/partner-program/votes/my`).then(res => setMyVotes(res.data)).catch(() => {});
    }
  }, [user]);

  const handleVote = async (partnerId, voteType) => {
    if (!user) { toast.error('Sign in to vote'); return; }
    try {
      const res = await axios.post(`${API}/partner-program/vote/${partnerId}`, { vote: voteType });
      // Update local votes
      setMyVotes(prev => {
        const updated = { ...prev };
        const current = updated[partnerId] || [];
        if (res.data.status === 'removed') {
          updated[partnerId] = current.filter(v => v !== voteType);
        } else {
          // Add vote, remove opposite if like/dislike switch
          let newVotes = [...current.filter(v => voteType === 'heart' ? v !== 'heart' : (v !== 'like' && v !== 'dislike')), voteType];
          updated[partnerId] = newVotes;
        }
        return updated;
      });
      // Update local partner counts
      fetchPartners();
    } catch { toast.error('Failed to vote'); }
  };

  const handleApply = async () => {
    if (!form.organization_name.trim()) { toast.error('Organization name is required'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/partner-program/apply`, form);
      toast.success('Application submitted! We\'ll review it shortly.');
      setApplyOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit application');
    } finally {
      setSubmitting(false);
    }
  };

  const filtered = searchQuery
    ? partners.filter(p =>
        p.organization_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        p.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        p.city?.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : partners;

  return (
    <div className="min-h-screen bg-background">
      <SEO title="Partner Program" description="Join the AADC Partner Program — rescues, breeders, businesses, and sponsors building a better world for dogs." url="/partner-program" />

      {/* Hero */}
      <div className="bg-gradient-to-r from-amber-700 to-amber-600 text-white py-10">
        <div className="container mx-auto px-4">
          <div className="flex items-center gap-3 mb-3">
            <h1 className="text-3xl sm:text-4xl font-heading font-medium flex items-center gap-3">
              <Handshake className="w-9 h-9" /> Partner Program
            </h1>
            <GuestAccessibleBadge className="bg-white/20 text-white" />
          </div>
          <p className="text-amber-100 text-lg max-w-2xl">
            Rescues, breeders, businesses, and sponsors working together to build a better world for dogs.
          </p>
          {user && (
            <div className="mt-4 flex flex-wrap gap-2">
              <Button onClick={() => setApplyOpen(true)} className="bg-white text-amber-800 hover:bg-white/90" data-testid="apply-partner-btn">
                <Plus className="w-4 h-4 mr-2" /> Apply to Be a Partner
              </Button>
              <Link
                to="/suggest-content?type=website"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-white/15 hover:bg-white/25 text-sm font-medium border border-white/30 transition"
                data-testid="suggest-partner-btn"
              >
                Suggest a Source →
              </Link>
            </div>
          )}
        </div>
      </div>

      <div className="container mx-auto px-4 py-6">
        <GuestBanner message="Sign in to apply as a partner organization" />

        {/* Stats */}
        <div className="flex flex-wrap gap-4 text-sm mb-6">
          <span className="flex items-center gap-1.5"><Building2 className="w-4 h-4 text-amber-600" /> <strong>{stats.total || 0}</strong> Partners</span>
          <span className="flex items-center gap-1.5"><Heart className="w-4 h-4 text-rose-600" /> <strong>{stats.by_type?.ngo || 0}</strong> Rescues</span>
          <span className="flex items-center gap-1.5"><Dog className="w-4 h-4 text-amber-600" /> <strong>{stats.by_type?.breeder || 0}</strong> Breeders</span>
          <span className="flex items-center gap-1.5"><Briefcase className="w-4 h-4 text-blue-600" /> <strong>{stats.by_type?.business || 0}</strong> Businesses</span>
          <span className="flex items-center gap-1.5"><ShieldCheck className="w-4 h-4 text-green-600" /> <strong>{stats.verified || 0}</strong> Verified</span>
        </div>

        {/* Top contributing partners — auto-hides when no redemptions yet */}
        <PartnerLeaderboard limit={5} className="mb-6" />

        {/* Controls */}
        <div className="flex flex-wrap gap-3 items-center mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input placeholder="Search partners..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-9 w-56" data-testid="partner-search" />
          </div>
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger className="w-40" data-testid="partner-type-filter">
              <SelectValue placeholder="All Types" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              <SelectItem value="ngo">Rescues / NGOs</SelectItem>
              <SelectItem value="breeder">Breeders</SelectItem>
              <SelectItem value="business">Businesses</SelectItem>
              <SelectItem value="sponsor">Sponsors</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Partner Grid */}
        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-amber-600" /></div>
        ) : filtered.length === 0 ? (
          <Card className="text-center py-16">
            <CardContent>
              <Handshake className="w-14 h-14 mx-auto text-muted-foreground/30 mb-4" />
              <h3 className="font-heading text-xl mb-2">No partners yet</h3>
              <p className="text-muted-foreground mb-4">Be the first to join the AADC Partner Program!</p>
              {user && (
                <Button onClick={() => setApplyOpen(true)}><Plus className="w-4 h-4 mr-1" /> Apply Now</Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {filtered.map(p => <PartnerCard key={p.id} partner={p} myVotes={myVotes} onVote={handleVote} user={user} />)}
          </div>
        )}

        {/* How it Works */}
        <div className="mt-12 mb-8">
          <h2 className="text-xl font-heading font-semibold mb-4">How the Partner Program Works</h2>
          <div className="grid md:grid-cols-3 gap-4">
            {[
              { step: '1', title: 'Apply', desc: 'Submit your organization details. We review applications within 48 hours.', icon: Plus },
              { step: '2', title: 'Get Verified', desc: 'Provide documentation to earn Verified status and build member trust.', icon: ShieldCheck },
              { step: '3', title: 'Grow Together', desc: 'Access features, get featured placement, and connect with our community.', icon: CheckCircle },
            ].map(s => (
              <Card key={s.step}>
                <CardContent className="p-5 text-center">
                  <div className="w-10 h-10 mx-auto mb-3 rounded-full bg-amber-100 flex items-center justify-center">
                    <s.icon className="w-5 h-5 text-amber-700" />
                  </div>
                  <h3 className="font-heading font-semibold mb-1">{s.title}</h3>
                  <p className="text-sm text-muted-foreground">{s.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>

      {/* Application Dialog */}
      <Dialog open={applyOpen} onOpenChange={setApplyOpen}>
        <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Handshake className="w-5 h-5" /> Partner Application
            </DialogTitle>
          </DialogHeader>
          <div
            className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900"
            data-testid="ngo-reward-notice"
          >
            <strong>Earn 1 free month of paid membership</strong> for every
            adoption agency / rescue / NGO you submit that gets approved
            (capped at 6 lifetime). Helping more dogs helps you too.
          </div>
          <div className="space-y-4 mt-2">
            <Input placeholder="Organization Name *" value={form.organization_name} onChange={(e) => setForm({ ...form, organization_name: e.target.value })} data-testid="apply-org-name" />
            <Select value={form.partner_type} onValueChange={(v) => setForm({ ...form, partner_type: v })}>
              <SelectTrigger data-testid="apply-type"><SelectValue placeholder="Type" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="ngo">Rescue / NGO</SelectItem>
                <SelectItem value="breeder">Breeder</SelectItem>
                <SelectItem value="business">Business</SelectItem>
                <SelectItem value="sponsor">Sponsor / Brand</SelectItem>
              </SelectContent>
            </Select>
            <Textarea placeholder="Description — tell us about your organization" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} />
            <Input placeholder="Website (optional)" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} />
            <div className="grid grid-cols-2 gap-3">
              <Input placeholder="Contact Name" value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} />
              <Input placeholder="Contact Email" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} />
            </div>
            <Input placeholder="Phone (optional)" value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} />
            <div className="grid grid-cols-2 gap-3">
              <Input placeholder="City" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} />
              <Input placeholder="State" value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} />
            </div>
            <Input placeholder="Services offered (e.g., adoption, training, boarding)" value={form.services_offered} onChange={(e) => setForm({ ...form, services_offered: e.target.value })} />
            <Input placeholder="Social media (Instagram, Facebook, etc.)" value={form.social_media} onChange={(e) => setForm({ ...form, social_media: e.target.value })} />
            <Input placeholder="501(c)(3) / Tax Exempt ID (optional — for NGOs)" value={form.tax_exempt_id} onChange={(e) => setForm({ ...form, tax_exempt_id: e.target.value })} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setApplyOpen(false)}>Cancel</Button>
            <Button onClick={handleApply} disabled={submitting} data-testid="submit-application-btn">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Handshake className="w-4 h-4 mr-1" />}
              Submit Application
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PartnerDirectory;
